import { observer } from "mobx-react-lite";

import { ExpressionNode } from "@/components/expression/ExpressionNode";
import { FlatNode } from "@/components/expression/utils/FlatNode";
import { NodeStyleProvider, useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { OperatorType } from "@/models/nodes/operators";
import { isLabelChild } from "@/models/nodes/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { twMerge } from "@/utils/twMerge";

import { NodeAttachments, useAttachmentShift } from "./utils/NodeAttachments";

import type { NodeRenderProps } from "./types";
import type { NodeAttachment } from "./utils/NodeAttachments";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { SupSubOperator } from "@/models/nodes/operators/types";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeSize } from "@corca/data";

type SupSubNodeProps = {
  node: NodeModel | null;
  index: number;
  parentNode?: InlineNodeModel;
  nodeRenderProps?: NodeRenderProps;
  attachmentNodes: NodeModel[];
  className?: string;
};

const isOverlappable = (node: NodeModel | null) => {
  if (node?.is(VariableNodeModel)) {
    return !node.hasParams;
  }

  return node?.is(NumberNodeModel) || node?.is(ConstantNodeModel);
};

const isAlwaysOverlappable = (node: NodeModel | null) => {
  return node?.is(FunctionNodeModel) && node.omitParentheses;
};

export const SupSubNode = observer(function SupSubNode({
  node,
  index,
  nodeRenderProps,
  attachmentNodes,
  parentNode,
  className,
}: SupSubNodeProps) {
  const { size: currentSize, view } = useNodeStyle();
  const isTextView = view === "text";
  const size = getSubNodesSize(node ?? attachmentNodes[0], currentSize);
  const level =
    nodeRenderProps?.level && Number.isInteger(nodeRenderProps.level) ? nodeRenderProps.level : 0;

  const sub = attachmentNodes.find((n) => n.is(OperatorType.SUBSCRIPT));
  const sup = attachmentNodes.find((n) => n.is(OperatorType.POWER));

  const addSpaceToCursor = node ? cursorIsBetweenSubSup(node, sup, sub) : false;

  const attachments: NodeAttachment[] = [
    {
      node: sup?.parameters[0],
      position: "right-top",
      nodeRenderProps: { ...nodeRenderProps, level: level + 1 },
    },
    {
      node: sub?.parameters[0],
      position: "right-bottom",
      nodeRenderProps: { ...nodeRenderProps, level: level + 1 },
    },
  ];

  const { shift, refs } = useAttachmentShift();

  const attachmentsEl = (
    <NodeAttachments
      {...refs}
      attachments={attachments}
      position="right"
      styleProviderValue={{ size }}
      className={twMerge("self-stretch", !isTextView && isOverlappable(node) && "-ml-0.75")}
      canOverlap={isAlwaysOverlappable(node) ? true : undefined}
    />
  );

  if (!node) {
    return (
      <div className={twMerge("inline-flex items-center", className)}>
        <div className="inline-flex items-center">
          <NodeStyleProvider placeholderVisible="always">
            {sup && parentNode && <FlatNode inlineNode={parentNode} index={sup.index} />}
            {sub && parentNode && <FlatNode inlineNode={parentNode} index={sub.index} />}
          </NodeStyleProvider>
        </div>
        {attachmentsEl}
      </div>
    );
  }

  return (
    <div className={twMerge("inline-flex items-center", className)}>
      <div
        className={twMerge("inline-flex items-center", addSpaceToCursor && !isTextView && "mr-1")}
        style={{ marginTop: shift }}
      >
        {parentNode ? (
          <FlatNode inlineNode={parentNode} index={index}>
            <ExpressionNode node={node} nodeRenderProps={nodeRenderProps} />
          </FlatNode>
        ) : (
          <ExpressionNode node={node} nodeRenderProps={nodeRenderProps} />
        )}
        {sup && parentNode && <FlatNode inlineNode={parentNode} index={sup.index} />}
        {sub && parentNode && <FlatNode inlineNode={parentNode} index={sub.index} />}
      </div>
      {attachmentsEl}
    </div>
  );
});

function cursorIsBetweenSubSup(node: NodeModel, sup?: SupSubOperator, sub?: SupSubOperator) {
  const { doc } = node;

  if (!node.parent) {
    return false;
  }

  return (
    doc.cursor.isCurrent(node.parent, sup?.index) || doc.cursor.isCurrent(node.parent, sub?.index)
  );
}

function getSubNodesSize(node: NodeModel, currentSize: NodeSize): NodeSize {
  if (isLabelChild(node)) {
    return "xxxs";
  }

  switch (currentSize) {
    case "xs": {
      return "xxs";
    }

    case "xxs": {
      return "xxs";
    }

    default: {
      return "xs";
    }
  }
}
